"use client";
import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, MapPin } from "lucide-react";
import Button from "../ui/Button";
import type { ISessionSlot } from "@/types/formation";
import { formatEuro } from "@/lib/utils";
import { MODALITY_LABELS } from "@/constants/formation";
import ContactModal from "../Home/ContactModal";
import EnrollmentModal from "./EnrollmentModal";
import { CONTROL_HEIGHT, INPUT_CLASS, LABEL_CLASS } from "./formStyles";

interface SessionsProps {
  sessions: ISessionSlot[];
  /** Formation these sessions belong to — shown as the enrollment modal's title. */
  formationTitle: string;
  /** Formation category, recorded as the lead's theme. */
  formationTheme?: string;
}

const INITIAL_COUNT = 5;

/** "08 juil." — the year is dropped unless `withYear`, so a range reads "08 juil. → 15 juil. 2026". */
function formatShortFrenchDate(date: string | null, withYear: boolean): string {
  if (!date) return "—";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    ...(withYear ? { year: "numeric" as const } : {}),
    timeZone: "UTC",
  }).format(parsed);
}

const Sessions: React.FC<SessionsProps> = ({
  sessions,
  formationTitle,
  formationTheme,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [enrollSession, setEnrollSession] = useState<ISessionSlot | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [filterLieu, setFilterLieu] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  const lieuOptions = useMemo(
    () => [...new Set(sessions.map((s) => s.location))],
    [sessions],
  );

  const filtered = useMemo(() => {
    return sessions.filter((session) => {
      if (filterLieu && session.location !== filterLieu) return false;
      // start_date/end_date are ISO ("YYYY-MM-DD"), same format as the date inputs, so plain string comparison works.
      if (
        filterDateFrom &&
        session.start_date &&
        session.start_date < filterDateFrom
      )
        return false;
      if (filterDateTo && session.end_date && session.end_date > filterDateTo)
        return false;
      return true;
    });
  }, [sessions, filterLieu, filterDateFrom, filterDateTo]);

  const displayed = showAll ? filtered : filtered.slice(0, INITIAL_COUNT);
  const hasMore = filtered.length > INITIAL_COUNT;
  const hasActiveFilters = filterLieu || filterDateFrom || filterDateTo;

  const resetFilters = () => {
    setFilterLieu("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setShowAll(false);
  };

  return (
    <section id="sessions" className="md:py-10 py-6 scroll-mt-36 lg:scroll-mt-24 px-4">
      <div className="bg-white rounded-3xl border border-[#40BFDE] md:px-16 px-4 md:pt-14 pt-10 md:pb-14 pb-8">
        {/* Section header */}
        <h2 className="font-red-hat text-[30px] md:text-[40px] leading-10 md:leading-13.25 font-light text-[#1B365E] text-center">
          Dates de sessions
        </h2>
        <p className="text-[#1B365E] font-bold text-[13px] md:text-[14px] leading-6 mt-3 mb-8 md:mb-10 max-w-2xl mx-auto text-center">
          Choisissez la session qui vous convient et inscrivez-vous directement
        </p>

        {/* Filters — sit straight on the card now, no filled panel */}
        <div className="flex flex-wrap items-end gap-4 md:gap-5 mb-6 md:mb-8">
          {/* Date from */}
          <div className="flex flex-col gap-1.5 flex-1 min-w-36">
            <label htmlFor="session-date-from" className={LABEL_CLASS}>
              À partir du
            </label>
            <input
              id="session-date-from"
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className={`${INPUT_CLASS} cursor-pointer`}
            />
          </div>

          {/* Date to */}
          <div className="flex flex-col gap-1.5 flex-1 min-w-36">
            <label htmlFor="session-date-to" className={LABEL_CLASS}>
              Jusqu’au
            </label>
            <input
              id="session-date-to"
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className={`${INPUT_CLASS} cursor-pointer`}
            />
          </div>

          {/* Lieu */}
          <div className="flex flex-col gap-1.5 flex-1 min-w-36">
            <label htmlFor="session-location" className={LABEL_CLASS}>
              Lieu de formation
            </label>
            <div className="relative">
              <select
                id="session-location"
                value={filterLieu}
                onChange={(e) => setFilterLieu(e.target.value)}
                className={`${INPUT_CLASS} appearance-none pr-8 cursor-pointer`}
              >
                <option value="">Tous les lieux</option>
                {lieuOptions.map((lieu) => (
                  <option key={lieu} value={lieu}>
                    {lieu}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5">
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                  <path
                    d="M1 1L5 5L9 1"
                    stroke="#1B365E"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Reset */}
          <AnimatePresence>
            {hasActiveFilters && (
              <motion.button
                type="button"
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ duration: 0.15 }}
                onClick={resetFilters}
                className={`flex items-center gap-1.5 px-1 ${CONTROL_HEIGHT} text-[#40BFDE] text-[13px] font-semibold hover:text-[#1B365E] transition-colors whitespace-nowrap cursor-pointer`}
              >
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
                  <path
                    d="M1.5 1.5L9.5 9.5M9.5 1.5L1.5 9.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                Réinitialiser
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* Empty state */}
        {filtered.length === 0 ? (
          <div className="text-center py-14 bg-[#EFEFEF] rounded-2xl">
            <p className="text-[#1D1D1D] text-[15px] mb-4">
              {sessions.length === 0
                ? "Aucune session n'est programmée pour le moment."
                : "Aucune session disponible pour les critères sélectionnés."}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center min-h-11 px-2 text-[#40BFDE] text-[14px] font-semibold underline hover:text-[#1B365E] transition-colors cursor-pointer"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Sessions list */}
            <div className="flex flex-col gap-3 md:gap-3.5">
              <AnimatePresence initial={false}>
                {displayed.map((session, index) => {
                  const isOnsite = session.session_type === "presentiel";
                  const sameYear =
                    !!session.start_date &&
                    !!session.end_date &&
                    session.start_date.slice(0, 4) ===
                      session.end_date.slice(0, 4);

                  return (
                    <motion.div
                      key={session.id}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{
                        duration: 0.25,
                        delay:
                          index >= INITIAL_COUNT
                            ? (index - INITIAL_COUNT) * 0.06
                            : 0,
                        ease: "easeInOut",
                      }}
                      className="overflow-hidden"
                    >
                      <div className="bg-white border border-[#EFEFEF] rounded-xl shadow-[1px_2px_8px_2px_#0000000D] px-4 md:px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4 md:gap-5">
                        {/* Calendar tile */}
                        <span className="hidden sm:flex size-9 shrink-0 items-center justify-center rounded-lg border border-[#EFEFEF] bg-[#EFEFEF] text-[#1B365E] text-[13px] font-bold leading-5">
                          <CalendarDays
                            className="size-4.5 text-[#1B365E] shrink-0"
                            strokeWidth={1.75}
                            aria-hidden="true"
                          />
                        </span>

                        {/* Dates + lieu */}
                        <div className="flex-1 min-w-0">
                          <p className="text-[#1D1D1D] text-[14px] md:text-[15px] font-bold leading-5">
                            {formatShortFrenchDate(
                              session.start_date,
                              !sameYear,
                            )}
                            <span className="mx-1.5 text-[#ABABAB] font-normal">
                              →
                            </span>
                            {formatShortFrenchDate(session.end_date, true)}
                          </p>

                          <div className="flex items-center flex-wrap gap-2 mt-1.5">
                            <span
                              className={`flex items-center gap-1 text-[12px] leading-4 ${
                                isOnsite ? "text-[#1B365E]" : "text-[#40BFDE]"
                              }`}
                            >
                              <MapPin
                                className="size-3.5 shrink-0"
                                strokeWidth={2}
                                aria-hidden="true"
                              />
                              {session.location}
                            </span>
                            <span
                              className={`rounded-full text-[11px] leading-4 px-2.5 py-1 shrink-0 ${
                                isOnsite
                                  ? "bg-[#CFF1F9] text-[#1B515E]"
                                  : "bg-[#EDEDED] text-[#1D1D1D]"
                              }`}
                            >
                              {MODALITY_LABELS[session.session_type] ??
                                session.session_type}
                            </span>
                          </div>
                        </div>

                        {/* Tarif — stacked and centred: was-price, price, then the discount badge */}
                        <div className="shrink-0 sm:text-center sm:min-w-30">
                          {!!session.original_price && (
                            <p className="text-[#ABABAB] text-[11px] line-through leading-4">
                              {formatEuro(session.original_price)} HT
                            </p>
                          )}
                          <p className="text-[#1B365E] text-[17px] font-bold leading-6">
                            {formatEuro(session.price)} HT
                          </p>
                          {!!session.discount_percent && (
                            <span className="inline-block bg-[#B6C93E] text-white text-[11px] font-bold px-2 py-0.5 rounded-full leading-4 mt-1">
                              -{session.discount_percent}%
                            </span>
                          )}
                        </div>

                        {/* CTA */}
                        <div className="shrink-0">
                          <Button
                            variant="gradient"
                            size="sm"
                            className="w-full sm:w-auto px-7!"
                            onClick={() => setEnrollSession(session)}
                          >
                            S’inscrire
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {/* Show more / less */}
            {hasMore && (
              <div className="flex justify-center mt-6">
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  aria-expanded={showAll}
                  className="group flex items-center gap-2 min-h-11 px-2 text-[#1B365E] text-[14px] font-semibold hover:text-[#40BFDE] transition-colors duration-200 cursor-pointer"
                >
                  {showAll
                    ? "Voir moins"
                    : `Voir plus de sessions (${filtered.length - INITIAL_COUNT} de plus)`}
                  <motion.svg
                    animate={{ rotate: showAll ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="shrink-0"
                    width="14"
                    height="9"
                    viewBox="0 0 14 9"
                    fill="none"
                  >
                    <path
                      d="M1 1L7 7L13 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </button>
              </div>
            )}
          </>
        )}

        {/* Contact CTA */}
        <div className="mt-10 md:mt-14 border border-[#FFBC42] bg-[#FFF7E9] rounded-2xl px-6 md:px-10 py-8 md:py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex-1 min-w-0">
            {/* No forced break: the card is only ~200px wide beside the button at
                md, so a hard-coded line split wrapped in the wrong places. */}
            <h3 className="font-red-hat text-[#1B365E] text-[22px] md:text-[30px] font-light leading-8 md:leading-10 tracking-[0.3px] mb-3 text-balance">
              Soyez prévenu dès qu&apos;une session vous correspond
            </h3>
            <p className="text-[#1D1D1D] text-[13px] md:text-[14px] leading-6 max-w-lg">
              Laissez-nous vos coordonnées, nous vous recontactons avec une solution adaptée ou dès qu&apos;une session est disponible.
            </p>
          </div>
          <Button
            variant="outline-gradient"
            size="md"
            onClick={() => setIsModalOpen(true)}
            className="shrink-0 w-full md:w-auto"
          >
            Nous contacter
          </Button>
        </div>
      </div>

      {/* "Je ne trouve pas ma session" → generic contact request */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        origin="contact"
        formationTitle={formationTitle}
        formationTheme={formationTheme}
      />

      {/* "S'inscrire" on a specific session → enrollment request */}
      <EnrollmentModal
        isOpen={enrollSession !== null}
        onClose={() => setEnrollSession(null)}
        formationTitle={formationTitle}
        formationTheme={formationTheme}
        session={enrollSession}
      />
    </section>
  );
};

export default Sessions;
